import { UseLoadingAction } from "@/common/composition/loading";
import { Ref, ref } from "@vue/composition-api";
import { delayBy } from "@/fp";
import { InformationType } from "@/common/models";
import { CategoryFamilies, build_category_families, family_label_ids } from "./categories";
import { SharedState } from "@/common/state";
import { AxisType, CoordinateInputTier3, TimeAxisRangeInput, TimeAxisType } from "@/gql/types";
import { GEN_AI_MOCK } from "./mock";
import { gen_ai_mock_enabled } from "./mock-mode";
import {
  AI_ORIGINATED_CODES,
  ActivityDirection,
  AiFileActivityId,
  EMPLOYEE_TO_AI_CODES,
  GenAiOverview,
  InfoCategoryId,
  PeriodTotals,
  empty_levels,
  empty_period_totals,
  empty_risk_cube,
  is_legacy_relation,
  relation_codes,
} from "./model";
import {
  AI_CONTENT_ITEMS_AVAILABLE,
  AxisCount,
  COUNT_PAGE_SIZE,
  ContentItemsFilter,
  InteractionCell,
  LevelKey,
  RelationCount,
  RiskCell,
  get_ai_label_rows,
  get_events_by_category,
  get_interaction_cells,
  get_risk_cells,
  shift_frame,
} from "./api";
import {
  GradedLabel,
  PeopleFold,
  StopCounts,
  empty_stop_counts,
  fold_people,
  fold_risk_cells,
  fold_visibility_rows,
  to_label_breakdown,
} from "./fold";
import { VirtualLabel, index_virtual_labels } from "./virtual-labels";

const [loading, set_loading] = UseLoadingAction<"init">(true, "init");

const empty: GenAiOverview = {
  content: [],
  info_breakdown: [],
  employee_breakdown: [],
  employees: [],
  agents_all: [],
  risk_cube: empty_risk_cube(),
  sensitivity_cube: empty_risk_cube(),
  employees_by_category: [],
  employees_by_level: empty_levels(),
  shares: {
    current: empty_period_totals(),
    previous: empty_period_totals(),
    period: "month",
  },
  activity_matrix: [],
  shared_by_employees: [],
  ai_file_activity: [],
  information_involved: [],
  agents_by_activity: [],
  agents_matrix: [],
  events: [],
};

const overview: Ref<GenAiOverview> = ref(empty);

/**
 * One request of the load. A card reads its own to tell "the backend returned
 * nothing" (a real zero) from "the request failed" (no number at all).
 */
export type GenAiRequest = "employees_risk" | "agents_risk" | "labels" | "interactions" | "pool";

const no_failures = (): Record<GenAiRequest, boolean> => ({
  employees_risk: false,
  agents_risk: false,
  labels: false,
  interactions: false,
  pool: false,
});

/** a card's subtitle when its request failed: no number, and why */
export const LOAD_FAILED_TEXT = "Couldn't load this card's data. The request failed.";

/** which of the current period's requests failed, by name */
const failed: Ref<Record<GenAiRequest, boolean>> = ref(no_failures());

/* ------------------------------------------------------------------ *
 * Sub-labels and their grades — the fallback when the label rows hold no
 * virtual labels (see fold_label_rows)
 * ------------------------------------------------------------------ */

/** a real label, not one of the tree's grouping nodes ("spi", "ms") */
const LABEL_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** the app numbers a label's sensitivity 1..3; the raw tree spells it out */
const level_of_sensitivity = (value: unknown): LevelKey => {
  const n = typeof value === "number" ? value : Number(value);
  if (Number.isFinite(n)) {
    return n >= 3 ? "high" : n >= 2 ? "medium" : n >= 1 ? "low" : "none";
  }
  const s = String(value || "").toLowerCase();
  if (s.startsWith("high")) return "high";
  if (s.startsWith("med")) return "medium";
  if (s.startsWith("low")) return "low";
  return "none";
};

/**
 * Every content label under the six categories, with the category it rolls up
 * to and its own sensitivity grade. Only ever used to read rows the server
 * returned — these ids are never sent in a query.
 */
const graded_labels = (
  by_label_id: Map<string, InfoCategoryId>
): Map<string, GradedLabel> => {
  const all = (SharedState.allInfoTypes.value || {}) as Record<string, InformationType>;
  const out = new Map<string, GradedLabel>();
  Object.values(all).forEach((t) => {
    if (!t || !LABEL_ID.test(t.id) || by_label_id.has(t.id)) return;
    if (t.labelType && t.labelType !== "Content_label") return;
    let parent = t.parentId;
    for (let hops = 0; parent && hops < 10; hops++) {
      const category = by_label_id.get(parent);
      if (category) {
        out.set(t.id, {
          category,
          level: level_of_sensitivity(t.sensitivityLevel),
          name: t.labelName || t.name || t.shortName || t.id,
        });
        return;
      }
      parent = all[parent] && all[parent].parentId;
    }
  });
  return out;
};

const rows_of = <T>(res: { success: boolean; value?: T[] } | null): T[] =>
  (res && res.success && res.value) || [];

/* ------------------------------------------------------------------ *
 * What the drill-downs point their lists at
 * ------------------------------------------------------------------ */

interface DrillIndex {
  /** each category's L0 labels — the same families the cards counted */
  families: CategoryFamilies;
  /** the 24 category x sensitivity virtual labels */
  virtual: Map<string, VirtualLabel>;
}

const drill_index = ref<DrillIndex>({
  families: build_category_families([]),
  virtual: new Map(),
});

type DrillScope = {
  relation?: AiFileActivityId;
  direction?: ActivityDirection;
  category?: InfoCategoryId;
};

/** the relation codes behind an activity, or behind a whole direction */
const codes_for = (scope: DrillScope): string[] =>
  relation_codes({ relation: scope.relation, direction: scope.direction });

/**
 * The events-list coordinate behind a card's number (the Visibility cards'
 * lists): the relation codes and the category's L0 label, so the list shows
 * what the card counted. Null in mock mode, where the drill scope feeds the
 * demo rows instead.
 */
export const gen_ai_scope_cord = (
  scope: DrillScope,
  z: TimeAxisRangeInput
): CoordinateInputTier3 | null => {
  if (gen_ai_mock_enabled()) return null;
  const l0s = scope.category ? drill_index.value.families.l0s_of[scope.category] : [];
  return {
    x: { type: AxisType.AiContentRelation, values: codes_for(scope) },
    y: l0s.length
      ? { type: AxisType.ContentLabel, values: [...l0s] }
      : { type: AxisType.None, values: [] },
    z,
  } as CoordinateInputTier3;
};

/** every risk level a content list can show, "no risk" included */
const ALL_SEVERITIES = [3, 2, 1, 0];
const ALL_LEVELS: LevelKey[] = ["high", "medium", "low", "none"];

/**
 * The `aiContentItems` filter behind a card's number — what its drill-down
 * lists. The month, the category, the relation and the risk levels travel in
 * the scoped coordinate (see api.ts §5):
 *
 * - category: the labels the card counted with — at a Visibility card's
 *   slider stop, the category's virtual label at that stop, otherwise its L0
 *   label (or all six, when the card counted every category);
 * - risk: the ObjectSeverity values the list shows — every level, "no risk"
 *   included, unless the card or the filter narrowed it;
 * - sensitivity, beyond the stop: the list filters the files on their own.
 *
 * Null in mock mode. A filter with no labels or no severities matches nothing.
 */
export const gen_ai_content_items_filter = (
  scope: DrillScope & { severities?: number[]; sensitivities?: LevelKey[] },
  frame: TimeAxisRangeInput
): ContentItemsFilter | null => {
  if (!AI_CONTENT_ITEMS_AVAILABLE || gen_ai_mock_enabled()) return null;
  return {
    relations: codes_for(scope),
    labels: gen_ai_stop_labels(scope.sensitivities || ALL_LEVELS, scope.category),
    severities: (scope.severities || ALL_SEVERITIES).map(String),
    frame,
  };
};

/* ------------------------------------------------------------------ *
 * The Visibility cards' slider stops (W3, W4, W5)
 *
 * Each stop is asked for on its own, with one label per category (Oct 2026):
 *
 *   All        the six information categories
 *   + Low      the categories' Low+ virtual labels
 *   + Medium   the Medium+ virtual labels
 *   High       the Highly sensitive virtual labels
 *
 * "All" is loaded with the dashboard; another stop the first time a slider
 * moves to it, and kept until the period changes.
 * ------------------------------------------------------------------ */

export type SensitivityStop = "high" | "medium" | "low" | "all";

/** the slider's position (1 = High .. 4 = All) as a stop */
export const STOP_AT: SensitivityStop[] = ["high", "medium", "low", "all"];

/** the stop a set of levels stands for: all four is "all", else the widest graded one */
export const stop_of_levels = (levels: readonly LevelKey[]): SensitivityStop => {
  if (ALL_LEVELS.every((l) => levels.includes(l))) return "all";
  const graded = (["high", "medium", "low"] as LevelKey[]).filter((l) => levels.includes(l));
  return (graded[graded.length - 1] || "high") as SensitivityStop;
};

/** the labels a stop asks with, for one category or all six */
const labels_for_stop = (stop: SensitivityStop, category?: InfoCategoryId | null): string[] => {
  const { families, virtual } = drill_index.value;
  if (stop === "all") {
    return category ? [...families.l0s_of[category]] : family_label_ids(families);
  }
  return [...virtual.values()]
    .filter((l) => l.level === stop && (!category || l.category === category))
    .map((l) => l.id);
};

/** the labels a card's number was counted with, for a drill-down to ask the same */
export const gen_ai_stop_labels = (
  levels: readonly LevelKey[],
  category?: InfoCategoryId | null
): string[] => labels_for_stop(stop_of_levels(levels), category);

/** the labels behind a Widget 5 row: its category at the slider's stop */
export const gen_ai_interaction_labels = (
  category: InfoCategoryId | null,
  levels: readonly LevelKey[]
): string[] => gen_ai_stop_labels(levels, category);

interface StopState<T> {
  data: Partial<Record<SensitivityStop, T>>;
  loading: Partial<Record<SensitivityStop, boolean>>;
  failed: Partial<Record<SensitivityStop, boolean>>;
}
const empty_stop_state = <T>(): StopState<T> => ({ data: {}, loading: {}, failed: {} });

/** W3 / W4: each category's AI activity by relation, per stop */
const visibility: Ref<StopState<StopCounts>> = ref(empty_stop_state<StopCounts>());
/** W5: the people per category, and in all, per stop */
const people: Ref<StopState<PeopleFold>> = ref(empty_stop_state<PeopleFold>());

/** what a stop's query needs, set by the load; a new load starts a new generation */
let stop_ctx: {
  generation: number;
  frame: TimeAxisRangeInput;
  category_of: (label: string) => InfoCategoryId | undefined;
  graded: Map<string, GradedLabel>;
} | null = null;
let generation = 0;

const set_stop = <T>(
  target: Ref<StopState<T>>,
  stop: SensitivityStop,
  patch: { data?: T; loading?: boolean; failed?: boolean }
) => {
  const s = target.value;
  target.value = {
    data: "data" in patch ? { ...s.data, [stop]: patch.data } : s.data,
    loading: { ...s.loading, [stop]: !!patch.loading },
    failed: "failed" in patch ? { ...s.failed, [stop]: !!patch.failed } : s.failed,
  };
};

/** W3 and W4's counts at a stop, asked for the first time the slider gets there */
const load_visibility_stop = async (stop: SensitivityStop) => {
  const ctx = stop_ctx;
  const s = visibility.value;
  if (!ctx || s.data[stop] || s.loading[stop]) return;
  const labels = labels_for_stop(stop);
  if (!labels.length) {
    set_stop(visibility, stop, { data: empty_stop_counts() });
    return;
  }
  set_stop(visibility, stop, { loading: true });
  const res = await get_ai_label_rows({
    relations: [...EMPLOYEE_TO_AI_CODES, ...AI_ORIGINATED_CODES],
    labels,
    frame: ctx.frame,
  });
  if (ctx.generation !== generation) return;
  if (!res.success) set_stop(visibility, stop, { failed: true });
  else set_stop(visibility, stop, { data: fold_visibility_rows(res.value, ctx.category_of).counts, failed: false });
};

/** W5's people at a stop, likewise */
const load_people_stop = async (stop: SensitivityStop) => {
  const ctx = stop_ctx;
  const s = people.value;
  if (!ctx || s.data[stop] || s.loading[stop]) return;
  const labels = labels_for_stop(stop);
  if (!labels.length) {
    set_stop(people, stop, { data: fold_people([], ctx.category_of) });
    return;
  }
  set_stop(people, stop, { loading: true });
  const res = await get_interaction_cells({ labels, frame: ctx.frame });
  if (ctx.generation !== generation) return;
  if (!res.success) set_stop(people, stop, { failed: true });
  else set_stop(people, stop, { data: fold_people(res.value, ctx.category_of), failed: false });
};

/**
 * W3's expand lists the sub-labels employees shared under, so it asks once,
 * when opened, for every label: the one call that groups by all of them.
 */
const load_info_breakdown = async () => {
  const ctx = stop_ctx;
  if (!ctx || overview.value.info_breakdown.length) return;
  const res = await get_ai_label_rows({ relations: EMPLOYEE_TO_AI_CODES, frame: ctx.frame });
  if (ctx.generation !== generation || !res.success) return;
  overview.value = { ...overview.value, info_breakdown: to_label_breakdown(res.value, ctx.graded) };
};

/** the demo's cubes, read the way a live stop reads: everything at that stop or above */
const LEVELS_AT: Record<SensitivityStop, LevelKey[]> = {
  high: ["high"],
  medium: ["high", "medium"],
  low: ["high", "medium", "low"],
  all: ["high", "medium", "low", "none"],
};
const fill_stops_from_mock = () => {
  STOP_AT.forEach((stop) => {
    const levels = LEVELS_AT[stop];
    const counts = empty_stop_counts();
    (Object.keys(counts) as InfoCategoryId[]).forEach((c) => {
      (Object.keys(counts[c]) as AiFileActivityId[]).forEach((a) => {
        counts[c][a] = levels.reduce((sum, l) => sum + GEN_AI_MOCK.sensitivity_cube[c][a][l], 0);
      });
    });
    set_stop(visibility, stop, { data: counts });
    const rows = {} as Record<InfoCategoryId, number>;
    GEN_AI_MOCK.employees_by_category.forEach((r) => {
      rows[r.id] = levels.reduce((sum, l) => sum + ((r.sensitivities && r.sensitivities[l]) || 0), 0);
    });
    set_stop(people, stop, {
      data: {
        rows,
        total: levels.reduce((sum, l) => sum + GEN_AI_MOCK.employees_by_level[l], 0),
      },
    });
  });
};

/* ------------------------------------------------------------------ *
 * Loading
 * ------------------------------------------------------------------ */

/**
 * Loads the dashboard, per the integration spec (see api.ts):
 *
 * - Risks cards (W1, W2): risky activity by relation, information category
 *   (the six L0 labels) and severity — UploadTo_AI for the employees' card,
 *   the AI-originated relations for the agents' card.
 * - Visibility cards (W3, W4): one call, every AI relation scoped by the six
 *   categories — the cards' "All" stop; the others are asked for when a
 *   slider moves (load_visibility_stop).
 * - Employees card (W5): the employees who interacted with AI under each
 *   category, counted per label (tier1count, y Employee, by XValue); its
 *   other stops likewise (load_people_stop).
 * - The share lines: the same calls for the previous period, as an explicit
 *   month, plus every event per category.
 *
 * Left empty rather than mocked: everything agent-shaped (`agents_*`), which
 * has no source in the schema.
 */
const init = async () => {
  set_loading(true, "init");

  const frame = SharedState.calculatedTimeFrame.value.frame as TimeAxisRangeInput;
  const period = frame.type === TimeAxisType.Week ? "week" : "month";

  failed.value = no_failures();
  // a new period: every stop is asked for again, and late answers are dropped
  generation += 1;
  stop_ctx = null;
  visibility.value = empty_stop_state<StopCounts>();
  people.value = empty_stop_state<PeopleFold>();

  if (gen_ai_mock_enabled()) {
    await delayBy(600);
    // the demo's pools are fixed, but "vs last …" still follows the frame
    overview.value = {
      ...GEN_AI_MOCK,
      shares: { ...GEN_AI_MOCK.shares, period },
    };
    fill_stops_from_mock();
    set_loading(false, "init");
    return;
  }

  // one mapping from L0 label to dashboard row for everything below: the
  // virtual labels, the pool, the breakdowns and the drill-downs
  const families = build_category_families(SharedState.l0InfoTypes.value as InformationType[]);
  const by_label_id = families.category_of;
  const pool_labels = family_label_ids(families);

  if (families.unmatched.length) {
    console.warn(
      "[gen-ai] no dashboard category for L0 info type(s):",
      families.unmatched.join(", ")
    );
  }

  if (!by_label_id.size) {
    overview.value = { ...empty, shares: { ...empty.shares, period } };
    set_loading(false, "init");
    return;
  }

  // The virtual labels name their category by its L0 id, so the same mapping
  // reads them: Other is the Personal row, and the Personal Information (PII)
  // set, which is in no row, is left out — the spec's 24.
  const virtual = index_virtual_labels(
    (SharedState.virtualLabels.value || []) as any[],
    (id: string) => by_label_id.get(id)
  );
  const all_virtual = [...virtual.keys()];
  // a row's label is a category (the "All" stop) or a virtual label (the others)
  const category_of_label = (label: string): InfoCategoryId | undefined => {
    const category = by_label_id.get(label);
    if (category) return category;
    const v = virtual.get(label);
    return v ? v.category : undefined;
  };
  if (all_virtual.length !== 24) {
    console.warn(
      `[gen-ai] expected 24 category x sensitivity virtual labels, found ${all_virtual.length}`
    );
  }
  const graded = graded_labels(by_label_id);
  const ALL_RELATION_CODES = [...EMPLOYEE_TO_AI_CODES, ...AI_ORIGINATED_CODES];
  const previous_frame = shift_frame(frame, 1);

  // The risk cards ask by information category — the six L0 labels — not by
  // the virtual labels: risk is graded by severity, so the sensitivity the
  // virtual labels carry is not part of the question, and asking with them
  // came back empty (corrected Oct 2026). Their slider only narrows severity.
  const risk = (relations: string[], at: TimeAxisRangeInput) =>
    pool_labels.length
      ? get_risk_cells({ relations, labels: pool_labels, frame: at })
      : Promise.resolve(null);

  // all issued before the first await, so they run together
  const requests = {
    employees_risk: risk(EMPLOYEE_TO_AI_CODES, frame),
    agents_risk: risk(AI_ORIGINATED_CODES, frame),
    employees_risk_before: risk(EMPLOYEE_TO_AI_CODES, previous_frame),
    agents_risk_before: risk(AI_ORIGINATED_CODES, previous_frame),
    // the Visibility cards open on "All": every relation of both directions
    // (W3 keeps the employee rows, W4 the AI ones) by the six categories
    labels: get_ai_label_rows({ relations: ALL_RELATION_CODES, labels: pool_labels, frame }),
    labels_before: get_ai_label_rows({
      relations: ALL_RELATION_CODES,
      labels: pool_labels,
      frame: previous_frame,
    }),
    interactions: get_interaction_cells({ labels: pool_labels, frame }),
    // the same families the numerators resolve to, for both periods
    pool: get_events_by_category({ labels: pool_labels, frame }),
    pool_before: get_events_by_category({ labels: pool_labels, frame: previous_frame }),
  };

  // a request that was issued and did not succeed; one that was never issued
  // (no virtual labels to ask about) has nothing to report
  const did_fail = async (request: Promise<{ success: boolean } | null>) => {
    const res = await request;
    return !!res && !res.success;
  };
  failed.value = {
    employees_risk: await did_fail(requests.employees_risk),
    agents_risk: await did_fail(requests.agents_risk),
    labels: await did_fail(requests.labels),
    interactions: await did_fail(requests.interactions),
    pool: await did_fail(requests.pool),
  };

  const risk_cells = [
    ...rows_of<RiskCell>(await requests.employees_risk),
    ...rows_of<RiskCell>(await requests.agents_risk),
  ];
  const risk_cells_before = [
    ...rows_of<RiskCell>(await requests.employees_risk_before),
    ...rows_of<RiskCell>(await requests.agents_risk_before),
  ];
  const label_rows = rows_of<RelationCount>(await requests.labels);
  const label_rows_before = rows_of<RelationCount>(await requests.labels_before);
  const interaction_cells = rows_of<InteractionCell>(await requests.interactions);

  // a list count that fills its page may have been cut short
  [
    ["risk (employees)", rows_of<RiskCell>(await requests.employees_risk)],
    ["risk (agents)", rows_of<RiskCell>(await requests.agents_risk)],
    ["labels", label_rows],
  ].forEach(([name, rows]) => {
    if ((rows as unknown[]).length >= COUNT_PAGE_SIZE) {
      console.warn(`[gen-ai] ${name}: ${COUNT_PAGE_SIZE} rows, the page cap — later rows are missing`);
    }
  });

  const risk_now = fold_risk_cells(risk_cells, by_label_id);
  const risk_before = fold_risk_cells(risk_cells_before, by_label_id);
  const labels_now = fold_visibility_rows(label_rows, category_of_label);
  const labels_before = fold_visibility_rows(label_rows_before, category_of_label);

  const current = empty_period_totals();
  const previous = empty_period_totals();
  const fill = (target: PeriodTotals, activity: PeriodTotals["activity"], risky: PeriodTotals["risky"]) => {
    target.activity = { ...activity };
    // The Risks cards' pool is the same activity, over the same six category
    // families: the risky counts by category label, the pool by the
    // categories' virtual labels.
    target.ai_events = { ...activity };
    target.risky = risky;
  };
  fill(current, labels_now.activity, risk_now.risky);
  fill(previous, labels_before.activity, risk_before.risky);

  const add_pool = (target: PeriodTotals, rows: AxisCount[]) =>
    rows.forEach((row) => {
      if (by_label_id.has(row.value)) target.events += row.count;
    });
  add_pool(current, rows_of<AxisCount>(await requests.pool));
  add_pool(previous, rows_of<AxisCount>(await requests.pool_before));

  drill_index.value = { families, virtual };
  stop_ctx = { generation, frame, category_of: category_of_label, graded };

  const unread = [
    ...new Set([...risk_now.unknown_relations, ...labels_now.unknown_relations]),
  ];
  const legacy = unread.filter(is_legacy_relation);
  const unknown = unread.filter((code) => !is_legacy_relation(code));
  if (legacy.length) {
    console.warn(
      "[gen-ai] legacy relation code(s) returned and left out of every count — no approved mapping (model.ts LEGACY_RELATION_CODES):",
      legacy.join(", ")
    );
  }
  if (unknown.length) {
    console.warn(
      "[gen-ai] relation code(s) outside the relation model, left out of every count (model.ts RELATION_CODE):",
      unknown.join(", ")
    );
  }
  if (risk_now.unknown_severities.length) {
    console.warn(
      "[gen-ai] ObjectSeverity value(s) not read as High, Medium or Low:",
      risk_now.unknown_severities.join(", ")
    );
  }

  const people_all = fold_people(interaction_cells, category_of_label);
  visibility.value = {
    data: { all: labels_now.counts },
    loading: {},
    failed: { all: failed.value.labels },
  };
  people.value = {
    data: { all: people_all },
    loading: {},
    failed: { all: failed.value.interactions },
  };

  // One line per load saying what came back, so "the dashboard shows zeros"
  // can be told apart from "the backend returned nothing for this period".
  console.info("[gen-ai] loaded", {
    frame,
    previous_frame,
    category_families: families.l0s_of,
    pii_family_not_in_any_row: families.pii_l0s,
    relations: { employee: EMPLOYEE_TO_AI_CODES, ai: AI_ORIGINATED_CODES },
    failed_requests: Object.keys(failed.value).filter((k) => failed.value[k as GenAiRequest]),
    virtual_labels: all_virtual.length,
    risk_labels: pool_labels,
    risk_rows: risk_cells.length,
    label_rows: label_rows.length,
    interaction_rows: interaction_cells.length,
    ai_activity: current.activity,
    risky: current.risky,
    // the Visibility cards' share = ai_activity[direction] / share_pool
    share_pool: { activities: current.events, labels: pool_labels, sensitivity: "any" },
  });

  overview.value = {
    ...empty,
    risk_cube: risk_now.cube,
    shares: { current, previous, period },
    // W3's expand asks for its sub-labels when it is opened (load_info_breakdown)
    info_breakdown: [],
  };

  set_loading(false, "init");
};

export const state = {
  init,
  loading,
  overview,
  failed,
  visibility,
  people,
  load_visibility_stop,
  load_people_stop,
  load_info_breakdown,
};
